import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import "@/themes/public.css";
import { useAuth } from "@/context/AuthContext";
import { ScopedMark, BrandWord } from "@/components/Logo";
import { ModeToggle } from "@/lib/mode";
import { MARKETS, useMarket } from "@/lib/market";
import { SOCIALS } from "@/components/Socials";

export const NAV = [["/play", "Play"], ["/communities", "Communities"], ["/business", "Business"], ["/partners", "Partners"], ["/experiences", "Experiences"], ["/feed", "Le Feed"]];

export function MarketSelect({ testId = "market-select" }) {
  const [m, setM] = useMarket();
  return (
    <select value={m} onChange={(e) => setM(e.target.value)} aria-label="Marché" className="p-input !w-auto !rounded-full !py-1.5 !pl-3 !pr-8 text-xs font-bold" style={{ fontSize: 13 }} data-testid={testId}>
      {MARKETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );
}

function Wordmark() {
  return (
    <Link to="/" className="flex items-center gap-2.5" data-testid="pub-logo">
      <ScopedMark scope="site" size={30} />
      <span className="text-[15px] uppercase leading-none tracking-[0.02em] p-t1" style={{ fontFamily: '"Barlow Condensed", "Arial Narrow", sans-serif', fontWeight: 800 }}><BrandWord scope="site" accentClass="p-accent" /></span>
    </Link>
  );
}

function Account({ mobile }) {
  const { user } = useAuth();
  if (user) return <Link to="/tableau-de-bord" className="p-btn !py-2 text-sm" data-testid={mobile ? "pub-resume-mobile" : "pub-resume"}>Reprendre ma partie <ArrowRight size={15} /></Link>;
  return <Link to="/connexion" className="p-btn-ghost !py-2 text-sm" data-testid={mobile ? "pub-login-mobile" : "pub-login"}>Connexion</Link>;
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="p-header" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-5 py-3">
        <Wordmark />
        <nav className="ml-6 hidden items-center gap-5 lg:flex" aria-label="Univers">
          {NAV.map(([to, l]) => <NavLink key={to} to={to} className="p-link py-2 text-sm font-semibold" data-testid={`pub-nav-${to.slice(1)}`}>{l}</NavLink>)}
        </nav>
        <div className="ml-auto hidden items-center gap-2 lg:flex">
          <ModeToggle compact testId="pub-mode-toggle" />
          <Account />
        </div>
        <button className="ml-auto grid h-11 w-11 place-items-center rounded-full p-t1 lg:hidden" onClick={() => setOpen(!open)} aria-label={open ? "Fermer le menu" : "Ouvrir le menu"} aria-expanded={open} data-testid="pub-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="border-t p-line px-5 pb-6 pt-2 lg:hidden" data-testid="pub-drawer">
          <nav className="flex flex-col" aria-label="Univers">
            {NAV.map(([to, l]) => <NavLink key={to} to={to} onClick={() => setOpen(false)} className="p-link border-b p-line py-3.5 font-display text-2xl font-extrabold uppercase" data-testid={`pub-drawer-${to.slice(1)}`}>{l}</NavLink>)}
          </nav>
          <div className="mt-5 flex flex-wrap items-center gap-3"><ModeToggle testId="pub-mode-toggle-mobile" /><Account mobile /></div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t p-line">
      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-12 md:grid-cols-[1.4fr_1fr_1fr]">
        <div><Wordmark /><p className="mt-3 max-w-sm text-sm p-t3">La plateforme NBA francophone : jeu entre amis, communautés, entreprises, partenaires et expériences. France et Canada.</p></div>
        <nav className="flex flex-col gap-2 text-sm" aria-label="Pied de page">{NAV.map(([to, l]) => <Link key={to} to={to} className="p-link">{l}</Link>)}</nav>
        <div className="flex flex-col gap-2 text-sm">
          <Link to="/confidentialite" className="p-link" data-testid="pub-footer-privacy">Confidentialité</Link>
          <Link to="/conditions" className="p-link" data-testid="pub-footer-terms">Conditions d'utilisation</Link>
          <div className="mt-2 flex gap-1">{SOCIALS.map(({ key, label, url, Icon }) => (
            <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={`${label} @mapetiteligue`} className="p-link grid h-11 w-11 place-items-center rounded-full" data-testid={`pub-footer-${key}`}><Icon size={16} /></a>))}</div>
        </div>
      </div>
      <p className="mx-auto max-w-7xl px-5 pb-8 text-xs p-t3">© {new Date().getFullYear()} Ma Petite Ligue · Non affilié à la NBA · Aucun pari d'argent.</p>
    </footer>
  );
}

export default function PublicShell() {
  return (
    <div className="pub overflow-x-clip" data-testid="public-shell">
      <Header />
      <main><Outlet /></main>
      <Footer />
    </div>
  );
}
